import {
  ActionIcon,
  Anchor,
  AppShell,
  Avatar,
  Badge,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconChartLine,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClipboardList,
  IconExternalLink,
  IconFileText,
  IconHistory,
  IconLogout,
  IconScale,
  IconSettings,
  IconUpload,
  IconUserCircle,
} from "@tabler/icons-react";
import { Suspense, lazy } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import type { UserResponse, UserRole } from "../api/types";
import omaiLogo from "../assets/omai-logo.png";
import { useAuth, useUser } from "../auth";
import { ThemeToggle } from "../components/ThemeToggle";
import { DEMO_LINKS, DEMO_MODE } from "../lib/mswEnv";

// The guided tour and its launcher exist only in the frontend-only demo build.
// `DEMO_MODE` is a build-time constant, so every other bundle drops the chunk.
const DemoTourRoot = DEMO_MODE ? lazy(() => import("../demo/tour/DemoTourRoot")) : null;

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  description?: string;
  disabled?: boolean;
  badge?: string;
  /** Roles allowed to see this nav item. Omitted = all roles. */
  allow?: UserRole[];
};

const NAV: ReadonlyArray<NavItem> = [
  {
    to: "/batches",
    label: "Batches",
    icon: IconClipboardList,
    description: "RFS + LCFS reporting periods",
  },
  {
    to: "/data",
    label: "Data",
    icon: IconChartLine,
    description: "Sensor & DUT time-series explorer",
  },
  { to: "/issues", label: "Issues", icon: IconAlertTriangle, description: "Data-quality findings" },
  {
    to: "/uploads",
    label: "Uploads",
    icon: IconUpload,
    description: "Manual data uploads for the current period",
    // Auditor is read-only; uploads are operations + compliance staff.
    allow: ["operations", "reviewer", "admin"],
  },
  { to: "/reports", label: "Reports", icon: IconFileText, description: "Generated artefacts" },
  {
    // One entry, four sections. The ledger, the lot queue behind it, and the
    // two rosters it merges are one workflow — four sidebar rows spread one
    // job across the nav and still left the lot inventory unreachable except
    // through an in-page link. Sections live at `/lcfs?tab=`.
    to: "/lcfs",
    label: "LCFS",
    icon: IconScale,
    description: "Allocation ledger, lots & rosters",
    allow: ["reviewer", "admin"],
  },
  {
    to: "/notifications",
    label: "Notifications",
    icon: IconBell,
    description: "Subscriptions & dispatch log",
  },
  {
    to: "/audit",
    label: "Audit",
    icon: IconHistory,
    description: "Chained event log",
    allow: ["reviewer", "admin"],
  },
  {
    to: "/config",
    label: "Config",
    icon: IconSettings,
    description: "Customer profile versions",
    allow: ["reviewer", "admin"],
  },
];

const NAV_WIDTH_EXPANDED = 260;
const NAV_WIDTH_COLLAPSED = 68;

export function ComplianceAppShell() {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useUser();

  const onNav = (to: string) => {
    navigate(to);
    closeMobile();
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: desktopOpened ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened },
      }}
      padding={{ base: "sm", sm: "lg" }}
    >
      <AppShell.Header withBorder style={{ backgroundColor: "var(--mantine-color-body)" }}>
        <Group h="100%" px={{ base: "sm", sm: "lg" }} justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Tooltip
              label={desktopOpened ? "Collapse sidebar" : "Expand sidebar"}
              position="bottom"
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                onClick={toggleDesktop}
                visibleFrom="sm"
                aria-label={desktopOpened ? "Collapse sidebar" : "Expand sidebar"}
              >
                {desktopOpened ? <IconChevronLeft size={18} /> : <IconChevronRight size={18} />}
              </ActionIcon>
            </Tooltip>
            <Group gap={10} wrap="nowrap">
              <img
                src={omaiLogo}
                alt="OMai"
                style={{ height: 26, width: "auto", display: "block" }}
              />
              <Divider orientation="vertical" my={4} />
              <Stack gap={0} visibleFrom="xs">
                <Text fw={600} size="md" lh={1.1}>
                  Compliance Platform
                </Text>
                <Text size="xs" c="dimmed" lh={1}>
                  RFS · LCFS
                </Text>
              </Stack>
            </Group>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {DEMO_LINKS.map((l) => (
              <Anchor
                key={l.tour}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                fw={500}
                visibleFrom="sm"
                data-tour={l.tour}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                {l.label}
                <IconExternalLink size={14} />
              </Anchor>
            ))}
            <UserMenu />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs" withBorder style={{ backgroundColor: "var(--mantine-color-body)" }}>
        <Stack gap={2}>
          {NAV.filter((item) => {
            if (!item.allow) return true;
            const user = currentUser;
            return user ? item.allow.includes(user.role) : false;
          }).map((item) => (
            <NavItemRow
              key={item.to}
              item={item}
              collapsed={!desktopOpened}
              active={location.pathname.startsWith(item.to)}
              onNav={onNav}
            />
          ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
        {DemoTourRoot ? (
          <Suspense fallback={null}>
            <DemoTourRoot />
          </Suspense>
        ) : null}
      </AppShell.Main>
    </AppShell>
  );
}

function NavItemRow({
  item,
  collapsed,
  active,
  onNav,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onNav: (to: string) => void;
}) {
  const Icon = item.icon;

  if (collapsed) {
    return (
      <Tooltip
        label={item.disabled ? `${item.label} · lands in ${item.badge}` : item.label}
        position="right"
        withArrow
        openDelay={250}
      >
        <UnstyledButton
          data-tour={`nav-${item.to.slice(1)}`}
          onClick={() => {
            if (!item.disabled) onNav(item.to);
          }}
          disabled={item.disabled === true}
          style={(t) => ({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: t.spacing.xs,
            borderRadius: t.radius.md,
            // Scheme-aware: the indigo "light variant" vars + body text vars
            // adapt to dark mode (the old t.colors.indigo[0] / gray[7] were
            // light-only and glared / went unreadable on the dark navbar).
            backgroundColor: active ? "var(--mantine-color-indigo-light)" : "transparent",
            color: active
              ? "var(--mantine-color-indigo-light-color)"
              : item.disabled
                ? "var(--mantine-color-dimmed)"
                : "var(--mantine-color-text)",
            cursor: item.disabled ? "not-allowed" : "pointer",
            opacity: item.disabled ? 0.55 : 1,
          })}
        >
          <ThemeIcon
            variant={active ? "light" : "subtle"}
            color={active ? "indigo" : "gray"}
            size={32}
            radius="md"
          >
            <Icon size={18} />
          </ThemeIcon>
        </UnstyledButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label={item.disabled ? `Lands in ${item.badge}` : (item.description ?? item.label)}
      position="right"
      openDelay={500}
      disabled={!item.description && !item.disabled}
    >
      <NavLink
        data-tour={`nav-${item.to.slice(1)}`}
        label={
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text size="sm" fw={active ? 600 : 500}>
              {item.label}
            </Text>
            {item.badge ? (
              <Badge size="xs" variant="default">
                {item.badge}
              </Badge>
            ) : null}
          </Group>
        }
        description={item.description}
        leftSection={
          <ThemeIcon
            size={28}
            radius="md"
            variant={active ? "light" : "default"}
            color={active ? "indigo" : "gray"}
          >
            <Icon size={16} />
          </ThemeIcon>
        }
        active={active}
        disabled={item.disabled === true}
        onClick={() => {
          if (!item.disabled) onNav(item.to);
        }}
        styles={{
          root: { borderRadius: 8 },
        }}
      />
    </Tooltip>
  );
}

function UserMenu() {
  const user = useUser();
  const { signOut, selectOrganization } = useAuth();
  const navigate = useNavigate();

  // `organizations` is optional on the wire so a backend that predates it still
  // parses; absent means "nothing to switch between", which is also the state
  // of every single-organization account.
  const organizations = user?.organizations ?? [];
  const actingOrganization = organizations.find(
    (organization) => organization.organization_id === user?.organization_id,
  );

  const switchTo = (organizationId: string) => {
    // False means a mutation is still in flight; switching would discard its
    // response, and a sign-off that vanished is worse than a delayed switch.
    if (!selectOrganization(organizationId)) {
      notifications.show({
        color: "orange",
        title: "Still saving",
        message: "Wait for the change in progress to finish before switching organization.",
      });
    }
  };

  if (!user) return null;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      // A rejected signOut means the API could not end the session and it is
      // still live — so do not navigate as though it ended. The provider sets
      // its error, but nothing in the protected UI renders that (only
      // LoginPage does, and we deliberately do not go there), so the user would
      // otherwise watch the menu close and conclude they were signed out.
      notifications.show({
        color: "red",
        title: "Sign-out failed",
        message:
          err instanceof Error
            ? err.message
            : "You are still signed in. Check your connection and try again.",
        autoClose: false,
      });
      return;
    }
    navigate("/login", { replace: true });
  };

  return (
    <Menu position="bottom-end" shadow="md" width={240}>
      <Menu.Target>
        <ActionIcon variant="subtle" size="lg" aria-label="User menu" data-tour="user-menu">
          <Group gap={6} wrap="nowrap">
            <Avatar size={28} radius="xl" color="indigo">
              {initialsFor(user)}
            </Avatar>
            <IconChevronDown size={14} />
          </Group>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              {user.display_name ?? user.email}
            </Text>
            <Text size="xs" c="dimmed" lh={1.1}>
              {user.email}
            </Text>
            <Group gap={6} mt={4}>
              <Badge variant="light" size="xs">
                {user.role}
              </Badge>
              {/* The organization this session acts in. Worth naming even when
                  there is nothing to switch to: it is not necessarily the org
                  the account belongs to, and every batch and report on screen
                  belongs to whichever one this is. */}
              {actingOrganization ? (
                <Text size="xs" c="dimmed" truncate maw={130}>
                  {actingOrganization.name}
                </Text>
              ) : null}
            </Group>
          </Stack>
        </Menu.Label>
        {organizations.length > 1 ? (
          <>
            <Menu.Divider />
            <Menu.Label>Switch organization</Menu.Label>
            {organizations.map((organization) => {
              const current = organization.organization_id === user.organization_id;
              return (
                <Menu.Item
                  key={organization.organization_id}
                  leftSection={current ? <IconCheck size={14} /> : <IconBuilding size={14} />}
                  disabled={current}
                  onClick={() => switchTo(organization.organization_id)}
                >
                  {organization.name}
                </Menu.Item>
              );
            })}
          </>
        ) : null}
        <Menu.Divider />
        <ThemeToggle />
        <Menu.Item leftSection={<IconUserCircle size={14} />} disabled>
          Profile
        </Menu.Item>
        {/* Demo mode has no login screen to return to, so there is nothing a
            sign-out could do but strand the presenter. */}
        {DEMO_MODE ? null : (
          <Menu.Item leftSection={<IconLogout size={14} />} onClick={handleSignOut} color="red">
            Sign out
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

function initialsFor(user: UserResponse): string {
  // Lazy-synced users have null display_name until edited — fall back to
  // the email's local part so the avatar still says something meaningful.
  const source = user.display_name ?? user.email.split("@")[0] ?? "";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return (source.slice(0, 2) || "??").toUpperCase();
}
